/**
 * The client error ring — what went wrong just before somebody pressed
 * "Report an issue".
 *
 * A reporter describes what they SAW; the cause is usually a failed request or
 * a script error a few seconds earlier that they never noticed. Asking them for
 * it is pointless, so the page keeps the last few of each in memory and the
 * report dialog attaches them automatically (`captureContext`).
 *
 * Install once per page (`installErrorRing`, idempotent). Hosts feed failed API
 * calls in through `recordFailedCall` from wherever their HTTP client sees
 * them — the platform's axios interceptor, the runtime client's fetch wrapper —
 * so this module carries no transport of its own.
 *
 * What it keeps (all bounded rings):
 * - script errors and unhandled rejections, with the stack PARSED into frames
 *   (`error-stack-parser`) so the analysis can name the file:line;
 * - resources that failed to load (a script, a stylesheet, an image);
 * - `console.error` / `console.warn` calls (React and the app log real faults
 *   there long before anything throws);
 * - failed API calls (fed by the host), with timing when known;
 * - BREADCRUMBS — the trail of what the person did: clicks (the control's
 *   label, never what they typed), navigation, and the errors above in order,
 *   so "steps to reproduce" are in the report even when the reporter wrote none;
 * - device facts (screen, connection, memory, online state).
 *
 * Nothing here leaves the browser until a report is filed, and what is kept is
 * deliberately thin: a URL loses its query VALUES (keys stay, so a developer
 * can still see which filter was applied) and messages are truncated, because
 * a ring buffer that quietly hoards tokens or personal data would be a worse
 * problem than the one it helps diagnose.
 */

import ErrorStackParser from 'error-stack-parser'

const MAX_ENTRIES = 20
const MAX_MESSAGE = 400
const MAX_BREADCRUMBS = 60

export interface StackFrame {
  fn?: string
  file?: string
  line?: number
  col?: number
}

export interface ClientErrorEntry {
  at: string
  message: string
  /** script | rejection | resource | console.error | console.warn | boundary */
  kind?: string
  source?: string
  /** First lines of the stack, when the browser gave one. */
  stack?: string
  /** The stack parsed into frames (top 8). */
  frames?: StackFrame[]
  /** How many times this same message repeated in a row. */
  count?: number
}

export interface Breadcrumb {
  at: string
  /** click | navigation | error | request | console */
  kind: string
  message: string
}

export interface FailedCallEntry {
  at: string
  method: string
  url: string
  status: number | null
  message?: string
  /** How long the call took, when the host knows. */
  duration_ms?: number
}

const clientErrors: ClientErrorEntry[] = []
const failedCalls: FailedCallEntry[] = []
const breadcrumbs: Breadcrumb[] = []
let installed = false

function push<T>(ring: T[], entry: T): void {
  ring.push(entry)
  if (ring.length > MAX_ENTRIES) ring.splice(0, ring.length - MAX_ENTRIES)
}

function clip(text: unknown, max = MAX_MESSAGE): string {
  const s = typeof text === 'string' ? text : (() => {
    try { return JSON.stringify(text) } catch { return String(text) }
  })()
  return s.length > max ? `${s.slice(0, max)}…` : s
}

/** Drop query VALUES (keep keys) and any fragment — enough to diagnose, not to leak. */
export function scrubUrl(raw: string): string {
  if (!raw) return ''
  try {
    const base = typeof window !== 'undefined' ? window.location.origin : 'http://local'
    const u = new URL(raw, base)
    const keys = Array.from(new Set(Array.from(u.searchParams.keys())))
    const qs = keys.length ? `?${keys.map((k) => `${k}=…`).join('&')}` : ''
    const sameOrigin = typeof window !== 'undefined' && u.origin === window.location.origin
    return `${sameOrigin ? '' : u.origin}${u.pathname}${qs}`
  } catch {
    return clip(raw.split('?')[0].split('#')[0], 300)
  }
}

function parseFrames(err: unknown): StackFrame[] | undefined {
  try {
    if (!err || typeof err !== 'object' || !(err as { stack?: string }).stack) return undefined
    return ErrorStackParser.parse(err as Error).slice(0, 8).map((f) => ({
      fn: f.functionName || undefined,
      file: f.fileName ? scrubUrl(f.fileName) : undefined,
      line: f.lineNumber,
      col: f.columnNumber,
    }))
  } catch {
    return undefined
  }
}

/** Add one step to the trail of what the person did. */
export function recordBreadcrumb(kind: string, message: unknown): void {
  try {
    const msg = clip(message, 200)
    const last = breadcrumbs[breadcrumbs.length - 1]
    if (last && last.kind === kind && last.message === msg) return
    breadcrumbs.push({ at: new Date().toISOString(), kind, message: msg })
    if (breadcrumbs.length > MAX_BREADCRUMBS) breadcrumbs.splice(0, breadcrumbs.length - MAX_BREADCRUMBS)
  } catch {
    // diagnostics never throw
  }
}

/** Record one client-side error (also usable by an error boundary — pass the
 *  Error as `error` to get its frames parsed). */
export function recordClientError(message: unknown, source?: string, stack?: string,
                                  kind = 'script', error?: unknown): void {
  try {
    const msg = clip(message)
    const last = clientErrors[clientErrors.length - 1]
    if (last && last.message === msg && last.kind === kind) {
      last.count = (last.count || 1) + 1
      last.at = new Date().toISOString()
      return
    }
    push(clientErrors, {
      at: new Date().toISOString(),
      message: msg,
      kind,
      source: source ? scrubUrl(source) : undefined,
      stack: stack ? clip(stack.split('\n').slice(0, 6).join('\n'), 800) : undefined,
      frames: parseFrames(error),
    })
    recordBreadcrumb('error', `${kind}: ${msg}`)
  } catch {
    // The ring is diagnostics; it must never become the error.
  }
}

/** Record one failed API call. `status` null = no response (network/abort). */
export function recordFailedCall(
  method: string | undefined, url: string | undefined,
  status: number | null | undefined, message?: unknown, durationMs?: number,
): void {
  try {
    const entry: FailedCallEntry = {
      at: new Date().toISOString(),
      method: String(method || 'GET').toUpperCase(),
      url: scrubUrl(String(url || '')),
      status: typeof status === 'number' ? status : null,
      message: message === undefined || message === null ? undefined : clip(message, 300),
      duration_ms: typeof durationMs === 'number' ? Math.round(durationMs) : undefined,
    }
    push(failedCalls, entry)
    recordBreadcrumb('request', `${entry.method} ${entry.url} → ${entry.status ?? 'no response'}`)
  } catch {
    // Same as above: never let diagnostics throw.
  }
}

/** A short label for the control a person clicked — its text or accessible
 *  name, never an input's value. */
function describeTarget(el: Element | null): string {
  if (!el) return ''
  const target = (el.closest?.('button, a, [role="button"], [role="tab"], [role="menuitem"], '
    + 'label, summary, input[type="checkbox"], input[type="radio"], select, [data-testid]') || el) as HTMLElement
  const tag = target.tagName?.toLowerCase() || 'element'
  const name = target.getAttribute?.('aria-label') || target.getAttribute?.('title')
    || (tag === 'input' || tag === 'select' || tag === 'textarea'
      ? (target.getAttribute('name') || target.id || '')
      : (target.innerText || target.textContent || '').trim())
  return `${tag}${name ? ` "${clip(name.replace(/\s+/g, ' '), 60)}"` : ''}`
}

/** Set while the ring itself writes to the console, so its own log line is
 *  not recorded a second time as a console error. */
let consoleQuiet = false

function hookConsole(level: 'error' | 'warn'): void {
  const original = console[level]
  if ((original as { __twastaRing?: boolean }).__twastaRing) return
  const wrapped = function (this: unknown, ...args: unknown[]) {
    if (consoleQuiet) return original.apply(this as Console, args as [])
    try {
      const err = args.find((a) => a instanceof Error) as Error | undefined
      const text = args.map((a) => (a instanceof Error ? a.message : typeof a === 'string' ? a : clip(a, 200)))
        .join(' ')
      recordClientError(text, undefined, err?.stack, `console.${level}`, err)
    } catch {
      // never break the console
    }
    return original.apply(this as Console, args as [])
  } as typeof console.error & { __twastaRing?: boolean }
  wrapped.__twastaRing = true
  console[level] = wrapped
}

function hookNavigation(): void {
  const note = () => recordBreadcrumb('navigation', scrubUrl(window.location.href))
  for (const name of ['pushState', 'replaceState'] as const) {
    const original = history[name]
    if ((original as { __twastaRing?: boolean }).__twastaRing) continue
    const wrapped = function (this: History, ...args: Parameters<History['pushState']>) {
      const out = original.apply(this, args)
      try { note() } catch { /* ignore */ }
      return out
    } as History['pushState'] & { __twastaRing?: boolean }
    wrapped.__twastaRing = true
    history[name] = wrapped
  }
  window.addEventListener('popstate', note)
}

/** Start capturing errors, failed resources, console faults, clicks and
 *  navigation. Safe to call often. */
export function installErrorRing(): void {
  if (installed || typeof window === 'undefined') return
  installed = true
  // Capture phase: a failed <script>/<link>/<img> dispatches a non-bubbling
  // error on the element, only visible to a capturing listener.
  window.addEventListener('error', (e: Event) => {
    const ev = e as ErrorEvent
    const target = e.target as (HTMLElement & { src?: string; href?: string }) | null
    if (target && target !== (window as unknown as EventTarget) && !(ev.message || ev.error)) {
      const src = target.src || target.href || ''
      if (src) recordClientError(`Failed to load ${target.tagName?.toLowerCase()} ${scrubUrl(src)}`,
        src, undefined, 'resource')
      return
    }
    const err = ev.error as { stack?: string } | undefined
    recordClientError(ev.message || String(ev.error), ev.filename, err?.stack, 'script', ev.error)
  }, true)
  window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
    const reason = e.reason as { message?: string; stack?: string } | undefined
    recordClientError(
      `Unhandled rejection: ${reason?.message || clip(reason)}`, undefined, reason?.stack,
      'rejection', reason,
    )
  })
  document.addEventListener('click', (e) => {
    try { recordBreadcrumb('click', describeTarget(e.target as Element)) } catch { /* ignore */ }
  }, true)
  window.addEventListener('online', () => recordBreadcrumb('network', 'back online'))
  window.addEventListener('offline', () => recordBreadcrumb('network', 'went offline'))
  try { hookConsole('error'); hookConsole('warn') } catch { /* locked-down console */ }
  try { hookNavigation() } catch { /* history not patchable */ }
  recordBreadcrumb('navigation', scrubUrl(window.location.href))
}

/** Snapshots of the two rings (copies — callers may keep them). */
export function recentClientErrors(): ClientErrorEntry[] {
  return clientErrors.map((e) => ({ ...e }))
}

export function recentFailedCalls(): FailedCallEntry[] {
  return failedCalls.map((e) => ({ ...e }))
}

export function recentBreadcrumbs(): Breadcrumb[] {
  return breadcrumbs.map((b) => ({ ...b }))
}

/** Device facts that change how a page behaves. */
function deviceInfo(): Record<string, unknown> {
  if (typeof window === 'undefined') return {}
  const nav = navigator as Navigator & {
    connection?: { effectiveType?: string; downlink?: number; saveData?: boolean }
    deviceMemory?: number
  }
  const perf = performance as Performance & { memory?: { usedJSHeapSize?: number } }
  return {
    screen: `${window.screen?.width}x${window.screen?.height}@${window.devicePixelRatio || 1}`,
    online: nav.onLine,
    connection: nav.connection ? {
      type: nav.connection.effectiveType, downlink: nav.connection.downlink,
      save_data: nav.connection.saveData,
    } : undefined,
    device_memory_gb: nav.deviceMemory,
    js_heap_mb: perf.memory?.usedJSHeapSize ? Math.round(perf.memory.usedJSHeapSize / 1048576) : undefined,
    page_age_s: Math.round(performance.now() / 1000),
    color_scheme: window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light',
  }
}

/** A short, human browser name + version from the user agent. */
function browserLabel(): string {
  if (typeof navigator === 'undefined') return ''
  const ua = navigator.userAgent || ''
  const pick = (re: RegExp, name: string) => {
    const m = ua.match(re)
    return m ? `${name} ${m[1]}` : ''
  }
  const name = pick(/Edg\/([\d.]+)/, 'Edge')
    || pick(/OPR\/([\d.]+)/, 'Opera')
    || pick(/Firefox\/([\d.]+)/, 'Firefox')
    || pick(/Chrome\/([\d.]+)/, 'Chrome')
    || pick(/Version\/([\d.]+).*Safari/, 'Safari')
  const os = /Windows/.test(ua) ? 'Windows'
    : /Mac OS X/.test(ua) ? 'macOS'
      : /Android/.test(ua) ? 'Android'
        : /(iPhone|iPad)/.test(ua) ? 'iOS'
          : /Linux/.test(ua) ? 'Linux' : ''
  return [name || clip(ua, 120), os].filter(Boolean).join(' on ')
}

export interface CapturedContext {
  screen?: string
  obj_name?: string
  url: string
  route: string
  browser: string
  viewport: string
  client_errors: ClientErrorEntry[]
  failed_calls: FailedCallEntry[]
  breadcrumbs: Breadcrumb[]
  device: Record<string, unknown>
  timezone: string
  locale: string
  [key: string]: unknown
}

/**
 * Everything the page knows about where the reporter is, merged with what the
 * host knows (`extra` — the open screen, object, record, app version). The
 * host's values win: it knows which screen is open; the page only knows a URL.
 */
export function captureContext(extra: Record<string, unknown> = {}): CapturedContext {
  const hasWindow = typeof window !== 'undefined'
  let timezone = ''
  try { timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || '' } catch { /* old engine */ }
  const base: CapturedContext = {
    screen: hasWindow ? (document.title || undefined) : undefined,
    url: hasWindow ? scrubUrl(window.location.href) : '',
    route: hasWindow ? window.location.pathname : '',
    browser: browserLabel(),
    viewport: hasWindow ? `${window.innerWidth}x${window.innerHeight}` : '',
    client_errors: recentClientErrors(),
    failed_calls: recentFailedCalls(),
    breadcrumbs: recentBreadcrumbs(),
    device: deviceInfo(),
    timezone,
    locale: typeof navigator !== 'undefined' ? (navigator.language || '') : '',
  }
  const out: CapturedContext = { ...base }
  for (const [k, v] of Object.entries(extra || {})) {
    if (v !== undefined && v !== null && v !== '') out[k] = v
  }
  return out
}

/** The `{error, componentStack}` React hands a root error callback. */
interface RootErrorInfo { componentStack?: string | null }

function messageOf(error: unknown): string {
  if (error instanceof Error) return error.message || error.name
  return typeof error === 'string' ? error : 'Render error'
}

/**
 * `createRoot` options that send EVERY render error to the ring: the ones an
 * error boundary caught (`onCaughtError` — a block, a transaction screen, a
 * designer) and the ones nothing caught (`onUncaughtError`). Wiring it at the
 * root reaches every boundary in the app — the shared page-runtime ones
 * included — without each one having to know about Support.
 *
 * Supplying these replaces React's own console reporting, so they log to the
 * console exactly as before as well.
 */
export function reactRootErrorOptions(): {
  onCaughtError: (error: unknown, info: RootErrorInfo) => void
  onUncaughtError: (error: unknown, info: RootErrorInfo) => void
} {
  const report = (kind: string) => (error: unknown, info: RootErrorInfo) => {
    consoleQuiet = true
    try {
      // eslint-disable-next-line no-console
      console.error(error, info?.componentStack || '')
    } finally {
      consoleQuiet = false
    }
    const stack = [(error as { stack?: string })?.stack, info?.componentStack].filter(Boolean).join('\n')
    recordClientError(messageOf(error), undefined, stack || undefined, kind, error)
  }
  return { onCaughtError: report('boundary'), onUncaughtError: report('uncaught') }
}
